// ============================================================
// Armes de RecubeICBM : lecture et écriture des sections weapons et weapon_rules
// ============================================================
// Fonctions pures, sans React ni accès réseau, testées par weaponsModel.test.ts (node --test).
//
// AUCUNE valeur du mod n'est écrite ici : valeurs par défaut, gabarit d'une entrée, bornes,
// catégories et missiles arrivent du serveur avec l'inspection du fichier (`catalog`, construit par
// RecubeIcbmWeapons.java, la seule table du panel, tenue à jour à partir de DefaultContent.cs du mod).
// Ce fichier ne porte que la logique :
//
//   - une entrée de `weapons` REMPLACE EN ENTIER la définition par défaut de même id : une clé
//     absente de l'entrée vaut le gabarit (`weaponTemplate`), pas l'arme par défaut ; une arme absente
//     du fichier garde sa définition par défaut ;
//   - la page édite les valeurs que le mod appliquerait (`Weapon`), et n'écrit dans le fichier que ce
//     qui change vraiment : une arme non touchée garde son entrée telle quelle (clés inconnues et
//     ordre compris), une arme revenue à ses valeurs par défaut quitte le fichier, une arme modifiée
//     garde son entrée où seules les valeurs changées sont réécrites.
// ============================================================

export type JsonObject = Record<string, unknown>;

/** Borne d'un nombre (validateur du mod) : `max` absent = pas de plafond. */
export interface NumberBound {
  min: number;
  max?: number;
  integer?: boolean;
}

/** Une clé de `params` : sa valeur quand elle est absente, ses bornes, drapeau 0 ou 1. */
export interface ParamSpec extends NumberBound {
  key: string;
  default: number;
  flag?: boolean;
}

export interface WeaponCategory {
  id: string;
  headshotMultiplier: number;
  emptyMagazine: string;
  reloadSound: string;
}

export interface CatalogMissile {
  id: string;
  name: string;
  tier: number;
}

export interface CatalogImage {
  containerId: string;
  containerName: string;
  version: string | null;
}

/** Ce que le serveur sait du mod en dehors du fichier (RecubeIcbmWeapons.catalog). */
export interface WeaponsCatalog {
  mod: string;
  file: string;
  /** Version du mod dont la table du serveur est tirée. */
  tableVersion: string;
  kinds: string[];
  categories: WeaponCategory[];
  /** Valeur de chaque clé absente d'une entrée de `weapons`. */
  weaponTemplate: JsonObject;
  /** Armes par défaut du mod, telles qu'il les écrit. */
  weapons: JsonObject[];
  fields: Record<string, NumberBound>;
  params: ParamSpec[];
  minAutoCooldownSeconds: number;
  rules: {
    /** Valeur de chaque règle absente de `weapon_rules`. */
    defaults: JsonObject;
    fields: Record<string, NumberBound>;
    /** Règles à liste fermée (claim_rule, animation_signal_mode) et leurs valeurs. */
    choices: Record<string, string[]>;
  };
  /** Missiles du fichier de référence (ceux par défaut, remplacés ou complétés par le fichier). */
  missiles: CatalogMissile[];
  /** Tier maximal d'un missile tiré à la main. */
  handMaxMissileTier: number;
  /** Version du mod dans l'image de chaque container qui porte le fichier. */
  images: CatalogImage[];
}

export const TEXT_FIELDS = ['item', 'name', 'kind', 'category', 'ammo_item', 'empty_magazine', 'sound', 'reload_sound'] as const;
export const NUMBER_FIELDS = [
  'damage', 'magazine', 'uses', 'cooldown_seconds', 'reload_seconds', 'range', 'projectile_speed', 'explosion_power',
] as const;
export const LIST_FIELDS = ['extra_missiles', 'levels', 'denied_levels'] as const;

export type TextField = (typeof TEXT_FIELDS)[number];
export type NumberField = (typeof NUMBER_FIELDS)[number];
export type ListField = (typeof LIST_FIELDS)[number];

/** Clés d'une entrée que la page gère ; toute autre clé du fichier est gardée telle quelle. */
const KNOWN_KEYS: readonly string[] = ['id', 'enabled', 'params', ...TEXT_FIELDS, ...NUMBER_FIELDS, ...LIST_FIELDS];

export const GUN = 'gun';
export const LAUNCHER = 'launcher';
export const MISSILE_LAUNCHER = 'missile_launcher';
const HEADSHOT_MULTIPLIER = 'headshot_multiplier';

/**
 * Une arme telle que le mod l'appliquerait : chaque champ a sa valeur (celle de l'entrée, sinon celle
 * du gabarit), `params` porte TOUTES les clés connues (celle de l'entrée, sinon son défaut), et
 * `unknownParams` les clés de params que le mod ne connaît pas (sans effet, gardées).
 */
export interface Weapon {
  id: string;
  item: string;
  name: string;
  kind: string;
  category: string;
  enabled: boolean;
  damage: number;
  magazine: number;
  ammo_item: string;
  empty_magazine: string;
  uses: number;
  cooldown_seconds: number;
  reload_seconds: number;
  range: number;
  projectile_speed: number;
  explosion_power: number;
  sound: string;
  reload_sound: string;
  extra_missiles: string[];
  levels: string[];
  denied_levels: string[];
  params: Record<string, number>;
  unknownParams: Record<string, number>;
}

export function isObject(v: unknown): v is JsonObject {
  return !!v && typeof v === 'object' && !Array.isArray(v);
}

/** Liste de noms comme le mod la lit (NameList) : espaces retirés, vides et doublons (casse ignorée) écartés. */
export function nameList(v: unknown): string[] {
  const out: string[] = [];
  if (!Array.isArray(v)) return out;
  for (const raw of v) {
    const name = typeof raw === 'string' ? raw.trim() : '';
    if (name && !out.some((o) => o.toLowerCase() === name.toLowerCase())) out.push(name);
  }
  return out;
}

export function categoryOf(catalog: WeaponsCatalog, id: string): WeaponCategory | undefined {
  return catalog.categories.find((c) => c.id === id);
}

/** Multiplicateur de tête d'une arme sans params.headshot_multiplier : celui de sa catégorie, sinon 1. */
export function categoryMultiplier(catalog: WeaponsCatalog, category: string): number {
  return categoryOf(catalog, category)?.headshotMultiplier ?? 1;
}

/** Valeur qu'une clé de params prend pour le mod : celle écrite, sinon son défaut (celui de la catégorie pour la tête). */
function effectiveParam(rawParams: JsonObject, spec: ParamSpec, category: string, catalog: WeaponsCatalog): number {
  const v = rawParams[spec.key];
  if (typeof v === 'number') return v;
  return spec.key === HEADSHOT_MULTIPLIER ? categoryMultiplier(catalog, category) : spec.default;
}

/** Identifiant d'une entrée du fichier (espaces retirés), '' si elle n'en a pas. */
export function entryId(entry: unknown): string {
  return isObject(entry) && typeof entry.id === 'string' ? entry.id.trim() : '';
}

/** Une entrée du fichier (ou une arme par défaut du catalogue) lue comme le mod la lirait. */
export function normalizeWeapon(raw: JsonObject, catalog: WeaponsCatalog): Weapon {
  const t = catalog.weaponTemplate;
  const text = (k: TextField): string => {
    const v = raw[k];
    if (typeof v === 'string') return v;
    return typeof t[k] === 'string' ? (t[k] as string) : '';
  };
  const num = (k: NumberField): number => {
    const v = raw[k];
    if (typeof v === 'number') return v;
    return typeof t[k] === 'number' ? (t[k] as number) : 0;
  };
  const category = text('category').trim().toLowerCase();
  const rawParams = isObject(raw.params) ? raw.params : {};
  const params: Record<string, number> = {};
  for (const spec of catalog.params) params[spec.key] = effectiveParam(rawParams, spec, category, catalog);
  // Une valeur qui n'est pas un nombre (le mod ne lirait plus le fichier) est gardée visible, en NaN.
  const unknownParams: Record<string, number> = {};
  for (const [k, v] of Object.entries(rawParams)) {
    if (!catalog.params.some((p) => p.key === k)) unknownParams[k] = typeof v === 'number' ? v : Number.NaN;
  }
  return {
    id: entryId(raw),
    item: text('item').trim(),
    name: text('name'),
    kind: text('kind').trim().toLowerCase(),
    category,
    enabled: typeof raw.enabled === 'boolean' ? raw.enabled : t.enabled !== false,
    damage: num('damage'),
    magazine: num('magazine'),
    ammo_item: text('ammo_item').trim(),
    empty_magazine: text('empty_magazine').trim(),
    uses: num('uses'),
    cooldown_seconds: num('cooldown_seconds'),
    reload_seconds: num('reload_seconds'),
    range: num('range'),
    projectile_speed: num('projectile_speed'),
    explosion_power: num('explosion_power'),
    sound: text('sound'),
    reload_sound: text('reload_sound'),
    extra_missiles: nameList(raw.extra_missiles),
    levels: nameList(raw.levels),
    denied_levels: nameList(raw.denied_levels),
    params,
    unknownParams,
  };
}

function sameList(a: string[], b: string[]): boolean {
  return a.length === b.length && a.every((x, i) => x === b[i]);
}

function sameNumbers(a: Record<string, number>, b: Record<string, number>): boolean {
  const ka = Object.keys(a);
  return ka.length === Object.keys(b).length && ka.every((k) => k in b && Object.is(a[k], b[k]));
}

/** Deux armes que le mod appliquerait à l'identique (il compare les id sans la casse). */
export function sameWeapon(a: Weapon, b: Weapon): boolean {
  return a.id.toLowerCase() === b.id.toLowerCase() && a.enabled === b.enabled
    && TEXT_FIELDS.every((k) => a[k] === b[k])
    && NUMBER_FIELDS.every((k) => Object.is(a[k], b[k]))
    && LIST_FIELDS.every((k) => sameList(a[k], b[k]))
    && sameNumbers(a.params, b.params)
    && sameNumbers(a.unknownParams, b.unknownParams);
}

// ==================== Écriture d'une arme ====================

/**
 * La valeur de cette clé dans l'entrée a-t-elle le type que le mod lit ? Absente : oui. Null : oui pour
 * un texte ou une liste (valeur par défaut), sauf kind, que le mod signale et remplace.
 */
function rawTypeOk(raw: JsonObject, key: string): boolean {
  const v = raw[key];
  if (v === undefined) return true;
  if (key === 'enabled') return typeof v === 'boolean';
  if ((NUMBER_FIELDS as readonly string[]).includes(key)) return typeof v === 'number';
  if (v === null) return key !== 'kind';
  if ((TEXT_FIELDS as readonly string[]).includes(key)) return typeof v === 'string';
  if ((LIST_FIELDS as readonly string[]).includes(key)) return Array.isArray(v) && v.every((x) => typeof x === 'string');
  return true;
}

/**
 * Copie de `obj` où `key` vaut `value`. Une clé nouvelle se place après la dernière clé présente qui
 * la précède dans `order` (l'ordre où le mod écrit ses clés), sinon en tête ; hors de `order`, à la fin.
 */
function withKey(obj: JsonObject, key: string, value: unknown, order: readonly string[]): JsonObject {
  if (key in obj) return { ...obj, [key]: value };
  const rank = order.indexOf(key);
  if (rank < 0) return { ...obj, [key]: value };
  let after: string | null = null;
  for (let i = rank - 1; i >= 0 && after === null; i--) {
    if (order[i] in obj) after = order[i];
  }
  const out: JsonObject = {};
  if (after === null) out[key] = value;
  for (const [k, v] of Object.entries(obj)) {
    out[k] = v;
    if (k === after) out[key] = value;
  }
  return out;
}

/**
 * Réécrit `base` (entrée du fichier, ou arme par défaut du catalogue) pour qu'elle vaille `next`, en ne
 * touchant que les clés dont la valeur appliquée par le mod change. Rend `base` lui-même (même
 * référence) si rien ne change : clés inconnues, ordre et écriture d'origine sont alors intacts.
 */
export function writeBack(base: JsonObject, next: Weapon, catalog: WeaponsCatalog): JsonObject {
  const before = normalizeWeapon(base, catalog);
  const order = Object.keys(catalog.weaponTemplate);
  let out = base;
  const put = (key: string, value: unknown) => { out = withKey(out, key, value, order); };

  // Une clé dont le type ferait échouer la lecture du fichier par le mod est toujours réécrite.
  if (before.enabled !== next.enabled || !rawTypeOk(base, 'enabled')) put('enabled', next.enabled);
  for (const k of TEXT_FIELDS) {
    if (before[k] !== next[k] || !rawTypeOk(base, k)) put(k, next[k]);
  }
  for (const k of NUMBER_FIELDS) {
    if (!Object.is(before[k], next[k]) || !rawTypeOk(base, k)) put(k, next[k]);
  }
  for (const k of LIST_FIELDS) {
    if (!sameList(before[k], next[k]) || !rawTypeOk(base, k)) put(k, [...next[k]]);
  }

  const baseParams = isObject(base.params) ? base.params : {};
  const paramOrder = catalog.params.map((p) => p.key);
  let params = baseParams;
  // La catégorie écrite décide du multiplicateur de tête d'une arme qui n'en porte pas.
  for (const spec of catalog.params) {
    const wanted = next.params[spec.key];
    if (wanted === undefined) continue;
    const current = baseParams[spec.key];
    const applied = effectiveParam(baseParams, spec, next.category, catalog);
    const badType = current !== undefined && typeof current !== 'number';
    if (!Object.is(applied, wanted) || badType) params = withKey(params, spec.key, wanted, paramOrder);
  }
  // Clés inconnues du mod : gardées telles quelles, sauf celles que la page a retirées.
  for (const k of Object.keys(baseParams)) {
    if (paramOrder.includes(k) || k in next.unknownParams) continue;
    params = Object.fromEntries(Object.entries(params).filter(([key]) => key !== k));
  }
  if (params !== baseParams || (base.params !== undefined && !isObject(base.params))) put('params', params);
  return out;
}

/**
 * Clés d'une entrée du fichier que le mod ne saurait pas lire (texte pour un nombre, null pour un
 * nombre...) : un seul de ces types fait échouer la lecture de tout le fichier, missiles coupés. La
 * page les réécrit toujours, avec la valeur affichée.
 */
export function unreadableKeys(raw: JsonObject, catalog: WeaponsCatalog): string[] {
  const out = ['enabled', ...TEXT_FIELDS, ...NUMBER_FIELDS, ...LIST_FIELDS].filter((k) => !rawTypeOk(raw, k));
  if (raw.params !== undefined && raw.params !== null && !isObject(raw.params)) out.push('params');
  if (isObject(raw.params)) {
    for (const spec of catalog.params) {
      const v = raw.params[spec.key];
      if (v !== undefined && typeof v !== 'number') out.push(`params.${spec.key}`);
    }
  }
  return out;
}

// ==================== Liste des armes : fichier, défauts, modifications de la page ====================

/** Modification en cours d'une arme, pas encore enregistrée. */
export interface WeaponEdit {
  value: Weapon;
  /** Retour aux valeurs par défaut demandé : l'entrée repart de la définition par défaut du mod. */
  reset?: boolean;
  /** Arme sur mesure retirée du fichier. */
  removed?: boolean;
}

/** Modifications par id d'arme en minuscules. */
export type WeaponEdits = Record<string, WeaponEdit>;

export interface DefaultWeapon {
  raw: JsonObject;
  value: Weapon;
}

export function defaultWeapons(catalog: WeaponsCatalog): Map<string, DefaultWeapon> {
  const out = new Map<string, DefaultWeapon>();
  for (const raw of catalog.weapons) {
    const value = normalizeWeapon(raw, catalog);
    out.set(value.id.toLowerCase(), { raw, value });
  }
  return out;
}

/**
 * Entrée à écrire pour une arme : celle du fichier si rien ne change, null si l'arme n'a pas (ou plus)
 * d'entrée (valeurs par défaut du mod, ou arme sur mesure retirée), sinon l'entrée réécrite. Une
 * entrée qui porte une valeur illisible pour le mod est toujours réécrite, même non modifiée.
 */
export function resolveEntry(
  raw: JsonObject | undefined,
  def: DefaultWeapon | undefined,
  edit: WeaponEdit | undefined,
  catalog: WeaponsCatalog,
): JsonObject | null {
  const unreadable = !!raw && unreadableKeys(raw, catalog).length > 0;
  if (!edit) {
    if (!raw) return null;
    return unreadable ? writeBack(raw, normalizeWeapon(raw, catalog), catalog) : raw;
  }
  if (edit.removed) return null;
  if (def && sameWeapon(edit.value, def.value)) {
    // Entrée déjà égale au défaut et non touchée : elle reste. Sinon l'arme n'a plus rien à dire au fichier.
    if (raw && !edit.reset && !unreadable && sameWeapon(edit.value, normalizeWeapon(raw, catalog))) return raw;
    return null;
  }
  const base = raw && !edit.reset ? raw : def?.raw;
  if (!base) return null;
  return writeBack(base, edit.value, catalog);
}

export type WeaponOrigin = 'default' | 'file' | 'custom';

export interface WeaponRow {
  /** Id en minuscules : clé des modifications. */
  key: string;
  id: string;
  /** default : absente du fichier ; file : arme par défaut redéfinie par le fichier ; custom : id inconnu du mod. */
  origin: WeaponOrigin;
  raw?: JsonObject;
  def?: DefaultWeapon;
  /** Valeurs affichées : celles de la page si elle a modifié l'arme, sinon celles lues. */
  value: Weapon;
  /** Entrée qui sera écrite (null : aucune), telle que resolveEntry la donne. */
  entry: JsonObject | null;
  /** L'enregistrement changera quelque chose pour cette arme. */
  edited: boolean;
  /** Arme sur mesure qui quittera le fichier. */
  removed: boolean;
  /** Les valeurs affichées diffèrent de la définition par défaut du mod. */
  differsFromDefault: boolean;
  /** Clés de l'entrée que la page ne gère pas (gardées telles quelles). */
  unknownKeys: string[];
  /** Clés de l'entrée que le mod ne saurait pas lire : réécrites avec la valeur affichée. */
  unreadable: string[];
}

/** Entrée de `weapons` que le mod ignorerait ou qui en masquerait une autre. */
export interface InvalidEntry {
  index: number;
  reason: string;
}

export interface WeaponList {
  rows: WeaponRow[];
  invalid: InvalidEntry[];
}

/**
 * Les armes dans l'ordre du mod : celles par défaut (redéfinies ou non par le fichier), puis les armes
 * sur mesure dans l'ordre du fichier.
 */
export function listWeapons(fileWeapons: unknown, edits: WeaponEdits, catalog: WeaponsCatalog): WeaponList {
  const defaults = defaultWeapons(catalog);
  const entries = new Map<string, JsonObject>();
  const invalid: InvalidEntry[] = [];
  const customs: string[] = [];
  const list = Array.isArray(fileWeapons) ? fileWeapons : [];
  list.forEach((e, index) => {
    const id = entryId(e);
    if (!isObject(e) || !id) {
      invalid.push({ index, reason: "sans id : le mod l'ignore" });
      return;
    }
    const key = id.toLowerCase();
    if (entries.has(key)) {
      invalid.push({ index, reason: `id « ${id} » en double : le mod ne garde que la dernière entrée` });
      return;
    }
    entries.set(key, e);
    if (!defaults.has(key)) customs.push(key);
  });

  const rows: WeaponRow[] = [];
  const add = (key: string) => {
    const raw = entries.get(key);
    const def = defaults.get(key);
    const edit = edits[key];
    const loaded = raw ? normalizeWeapon(raw, catalog) : def!.value;
    const value = edit && !edit.removed ? edit.value : loaded;
    const entry = resolveEntry(raw, def, edit, catalog);
    rows.push({
      key,
      id: loaded.id,
      origin: !def ? 'custom' : raw ? 'file' : 'default',
      raw,
      def,
      value,
      entry,
      edited: entry !== (raw ?? null),
      removed: !!edit?.removed,
      differsFromDefault: !!def && !sameWeapon(value, def.value),
      unknownKeys: raw ? Object.keys(raw).filter((k) => !KNOWN_KEYS.includes(k)) : [],
      unreadable: raw ? unreadableKeys(raw, catalog) : [],
    });
  };
  for (const key of defaults.keys()) add(key);
  for (const key of customs) add(key);
  return { rows, invalid };
}

/**
 * Tableau `weapons` à écrire : les entrées du fichier dans leur ordre (inchangées, réécrites ou
 * retirées), puis les armes par défaut que la page a modifiées et qui n'y étaient pas. Les entrées
 * que le mod ignorerait (`invalid`) restent, sauf si `dropInvalid`.
 */
export function buildWeapons(fileWeapons: unknown, edits: WeaponEdits, catalog: WeaponsCatalog, dropInvalid = false): unknown[] {
  const { rows, invalid } = listWeapons(fileWeapons, edits, catalog);
  const byKey = new Map(rows.map((r) => [r.key, r]));
  const skipped = new Set(invalid.map((i) => i.index));
  const out: unknown[] = [];
  const list = Array.isArray(fileWeapons) ? fileWeapons : [];
  list.forEach((e, index) => {
    if (skipped.has(index)) {
      if (!dropInvalid) out.push(e);
      return;
    }
    const row = byKey.get(entryId(e).toLowerCase());
    if (row?.entry) out.push(row.entry);
  });
  for (const row of rows) {
    if (!row.raw && row.entry) out.push(row.entry);
  }
  return out;
}

// ==================== Règles communes (weapon_rules) ====================

export type RuleValue = boolean | number | string | string[];
/** Règles telles que le mod les appliquerait : chaque clé connue a sa valeur (fichier, sinon défaut). */
export type WeaponRules = Record<string, RuleValue>;

function ruleFromRaw(raw: unknown, fallback: unknown): RuleValue {
  if (Array.isArray(fallback)) return raw === undefined || raw === null ? nameList(fallback) : nameList(raw);
  if (typeof fallback === 'boolean') return typeof raw === 'boolean' ? raw : fallback;
  if (typeof fallback === 'number') return typeof raw === 'number' ? raw : fallback;
  const text = typeof raw === 'string' ? raw : typeof fallback === 'string' ? fallback : '';
  return text.trim();
}

function sameRule(a: RuleValue, b: RuleValue): boolean {
  if (Array.isArray(a) || Array.isArray(b)) return Array.isArray(a) && Array.isArray(b) && sameList(a, b);
  return Object.is(a, b);
}

export function normalizeRules(raw: unknown, catalog: WeaponsCatalog): WeaponRules {
  const file = isObject(raw) ? raw : {};
  const out: WeaponRules = {};
  for (const [k, fallback] of Object.entries(catalog.rules.defaults)) {
    const value = ruleFromRaw(file[k], fallback);
    // Le mod met en minuscules la valeur d'une règle à liste fermée.
    out[k] = catalog.rules.choices[k] && typeof value === 'string' ? value.toLowerCase() : value;
  }
  return out;
}

/** `closed` : règle à liste fermée, que le mod signale et remplace quand elle est écrite à null. */
function ruleTypeOk(raw: unknown, fallback: unknown, closed = false): boolean {
  if (raw === undefined) return true;
  if (typeof fallback === 'boolean') return typeof raw === 'boolean';
  if (typeof fallback === 'number') return typeof raw === 'number';
  if (raw === null) return !closed;
  if (Array.isArray(fallback)) return Array.isArray(raw) && raw.every((x) => typeof x === 'string');
  return typeof raw === 'string';
}

/** Règles du fichier que le mod ne saurait pas lire (mauvais type) : réécrites avec la valeur affichée. */
export function unreadableRules(raw: unknown, catalog: WeaponsCatalog): string[] {
  const file = isObject(raw) ? raw : {};
  return Object.keys(catalog.rules.defaults).filter((k) => !ruleTypeOk(file[k], catalog.rules.defaults[k], !!catalog.rules.choices[k]));
}

/**
 * Section `weapon_rules` à écrire : celle du fichier (même référence si rien ne change) où seules les
 * règles dont la valeur appliquée change sont réécrites ou ajoutées. Une règle absente du fichier et
 * restée à sa valeur par défaut n'est pas écrite. Undefined : pas de section, et rien à y mettre.
 */
export function buildRules(raw: unknown, next: WeaponRules, catalog: WeaponsCatalog): JsonObject | undefined {
  const file = isObject(raw) ? raw : {};
  const before = normalizeRules(raw, catalog);
  const order = Object.keys(catalog.rules.defaults);
  let out = file;
  for (const k of order) {
    if (!(k in next)) continue;
    if (!sameRule(before[k], next[k]) || !ruleTypeOk(file[k], catalog.rules.defaults[k], !!catalog.rules.choices[k])) {
      out = withKey(out, k, Array.isArray(next[k]) ? [...(next[k] as string[])] : next[k], order);
    }
  }
  if (out === file) return isObject(raw) ? raw : undefined;
  return out;
}

// ==================== Sections envoyées au serveur ====================

export interface LoadedSections {
  weapons?: unknown;
  weapon_rules?: unknown;
}

/**
 * Sections à enregistrer. Une section absente du fichier n'est créée que si la page a quelque chose à
 * y écrire ; une section présente est toujours renvoyée (les autres containers s'alignent dessus).
 */
export function buildSections(
  loaded: LoadedSections,
  edits: WeaponEdits,
  rules: WeaponRules,
  catalog: WeaponsCatalog,
  dropInvalid = false,
): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  const weapons = buildWeapons(loaded.weapons, edits, catalog, dropInvalid);
  if (loaded.weapons !== undefined || weapons.length > 0) out.weapons = weapons;
  const weaponRules = buildRules(loaded.weapon_rules, rules, catalog);
  if (weaponRules !== undefined) out.weapon_rules = weaponRules;
  return out;
}

// ==================== Unités et valeurs dérivées ====================

function round(v: number, digits: number): number {
  const f = 10 ** digits;
  return Math.round(v * f) / f;
}

/** Cadence en tirs par seconde d'un délai entre deux tirs ; null sans délai (ou délai illisible). */
export function fireRate(cooldownSeconds: number): number | null {
  return cooldownSeconds > 0 && Number.isFinite(cooldownSeconds) ? round(1 / cooldownSeconds, 3) : null;
}

/** Délai entre deux tirs d'une cadence, arrondi au millionième comme le mod (1 / cadence). */
export function cooldownOfRate(tirsParSeconde: number): number {
  return tirsParSeconde > 0 && Number.isFinite(tirsParSeconde) ? round(1 / tirsParSeconde, 6) : Number.NaN;
}

/** Dégâts d'une balle à la tête (WeaponMath.HeadshotDamage) : la valeur dédiée, sinon dégâts x multiplicateur. */
export function headshotDamage(w: Weapon): number {
  const dedicated = w.params.headshot_damage ?? 0;
  if (dedicated > 0) return dedicated;
  const multiplier = w.params[HEADSHOT_MULTIPLIER] ?? 1;
  return round(w.damage * (multiplier > 0 ? multiplier : 1), 3);
}

export function isAutomatic(w: Weapon): boolean {
  return w.kind === GUN && (w.params.auto ?? 0) > 0;
}

/**
 * Changement de catégorie : ce que l'arme tenait de son ancienne catégorie (multiplicateur de tête,
 * chargeur vide, son de rechargement) suit la nouvelle ; une valeur réglée à la main est gardée.
 */
export function withCategory(w: Weapon, category: string, catalog: WeaponsCatalog): Weapon {
  const from = categoryOf(catalog, w.category);
  const to = categoryOf(catalog, category);
  const next: Weapon = { ...w, category, params: { ...w.params } };
  if (w.params[HEADSHOT_MULTIPLIER] === categoryMultiplier(catalog, w.category)) {
    next.params[HEADSHOT_MULTIPLIER] = categoryMultiplier(catalog, category);
  }
  if (from && to) {
    if (w.empty_magazine === from.emptyMagazine) next.empty_magazine = to.emptyMagazine;
    if (w.reload_sound === from.reloadSound) next.reload_sound = to.reloadSound;
  }
  return next;
}

// ==================== Contrôles (les mêmes que le serveur, pour les dire champ par champ) ====================

export interface Problem {
  /** Champ concerné : `damage`, `params.pellets`, `item`... */
  field: string;
  message: string;
}

const IDENTIFIER = /^[a-z0-9_.\-/]+:[a-z0-9_.\-/]+$/;

/** Identifiant espace:nom en minuscules, comme le validateur du mod. */
export function isIdentifier(value: string): boolean {
  return IDENTIFIER.test(value);
}

function formatBound(b: NumberBound): string {
  return b.max === undefined ? `${b.min} au moins` : `de ${b.min} à ${b.max}`;
}

/** Message si le nombre sort de ses bornes, sinon null. */
export function boundProblem(value: number, b: NumberBound): string | null {
  if (typeof value !== 'number' || !Number.isFinite(value)) return 'nombre attendu';
  if (b.integer && !Number.isInteger(value)) return 'nombre entier attendu';
  if (value < b.min || (b.max !== undefined && value > b.max)) return `hors bornes (${formatBound(b)})`;
  return null;
}

/**
 * Ce que le serveur refuserait pour cette arme (et que le mod corrigerait ou ne saurait pas lire).
 * `others` : les autres armes telles qu'elles seront appliquées, pour l'objet déjà pris.
 */
export function weaponProblems(w: Weapon, catalog: WeaponsCatalog, others: Weapon[] = []): Problem[] {
  const out: Problem[] = [];
  const add = (field: string, message: string) => out.push({ field, message });
  for (const k of NUMBER_FIELDS) {
    const bound = catalog.fields[k];
    const bad = bound ? boundProblem(w[k], bound) : Number.isFinite(w[k]) ? null : 'nombre attendu';
    if (bad) add(k, bad);
  }
  if (!catalog.kinds.includes(w.kind)) add('kind', `type inconnu (${catalog.kinds.join(', ')})`);
  if (w.category && !categoryOf(catalog, w.category)) {
    add('category', `catégorie inconnue (${catalog.categories.map((c) => c.id).join(', ')} ou aucune)`);
  }
  if (!isIdentifier(w.item)) add('item', 'identifiant espace:nom en minuscules attendu (ex. ng:ak47)');
  else {
    const taken = others.find((o) => o.id.toLowerCase() !== w.id.toLowerCase() && o.item.toLowerCase() === w.item.toLowerCase());
    if (taken) add('item', `objet déjà utilisé par ${taken.id} : il ne déclencherait que la première des deux armes`);
  }
  const gun = w.kind === GUN;
  if (gun && Number.isFinite(w.magazine) && w.magazine < 1) add('magazine', 'une arme à feu a au moins 1 balle par chargeur');
  if (!gun && catalog.kinds.includes(w.kind) && Number.isFinite(w.projectile_speed) && w.projectile_speed <= 0) {
    add('projectile_speed', 'un lanceur a une vitesse de projectile supérieure à 0');
  }
  if (!w.ammo_item) {
    if (gun || w.kind === LAUNCHER) add('ammo_item', "une arme à feu ou un lanceur de roquettes a besoin d'une munition");
  } else if (!isIdentifier(w.ammo_item)) {
    add('ammo_item', 'identifiant espace:nom en minuscules attendu');
  }
  if (w.empty_magazine) {
    if (!isIdentifier(w.empty_magazine)) add('empty_magazine', 'identifiant espace:nom en minuscules attendu');
    else if (w.empty_magazine.toLowerCase() === w.ammo_item.toLowerCase()) {
      add('empty_magazine', 'le chargeur vide ne peut pas être le chargeur plein : chaque rechargement le rendrait');
    }
  }
  for (const spec of catalog.params) {
    const v = w.params[spec.key];
    if (v === undefined) continue;
    const field = `params.${spec.key}`;
    if (spec.flag) {
      if (v !== 0 && v !== 1) add(field, '0 ou 1 attendu');
      continue;
    }
    const bad = boundProblem(v, spec);
    if (bad) add(field, bad);
  }
  for (const [k, v] of Object.entries(w.unknownParams)) {
    if (!Number.isFinite(v) || v < 0) add(`params.${k}`, 'nombre positif ou nul attendu');
  }
  if (isAutomatic(w) && Number.isFinite(w.cooldown_seconds) && w.cooldown_seconds < catalog.minAutoCooldownSeconds
    && !out.some((p) => p.field === 'cooldown_seconds')) {
    const maxRate = fireRate(catalog.minAutoCooldownSeconds);
    add('cooldown_seconds', `trop court pour un tir automatique : ${catalog.minAutoCooldownSeconds} s au moins (${maxRate} tirs par seconde au plus)`);
  }
  for (const id of w.extra_missiles) {
    const missile = catalog.missiles.find((m) => m.id.toLowerCase() === id.toLowerCase());
    if (!missile) add('extra_missiles', `missile « ${id} » inconnu de ce fichier`);
    else if (missile.tier > catalog.handMaxMissileTier) add('extra_missiles', `${missile.id} est un T${missile.tier}, jamais tiré à la main`);
  }
  return out;
}

/** Ce que le mod accepte mais qui n'a pas d'effet ou surprend : signalé sans bloquer. */
export function weaponWarnings(w: Weapon): Problem[] {
  const out: Problem[] = [];
  if (isAutomatic(w) && (w.params.burst ?? 1) > 1) {
    out.push({ field: 'params.burst', message: 'rafale ignorée en tir automatique : la cadence suit le délai entre deux tirs' });
  }
  for (const k of Object.keys(w.unknownParams)) {
    out.push({ field: `params.${k}`, message: 'clé inconnue du mod, sans effet (le mod la signale au chargement)' });
  }
  if (w.kind === GUN && Number.isFinite(w.range) && (w.params.falloff_start ?? 0) > w.range) {
    out.push({ field: 'params.falloff_start', message: 'au-delà de la portée : aucune baisse des dégâts avec la distance' });
  }
  return out;
}

/** Ce que le serveur refuserait dans les règles communes. */
export function ruleProblems(rules: WeaponRules, catalog: WeaponsCatalog): Problem[] {
  const out: Problem[] = [];
  for (const [k, bound] of Object.entries(catalog.rules.fields)) {
    const v = rules[k];
    if (v === undefined) continue;
    const bad = typeof v === 'number' ? boundProblem(v, bound) : 'nombre attendu';
    if (bad) out.push({ field: k, message: bad });
  }
  for (const [k, choices] of Object.entries(catalog.rules.choices)) {
    const v = rules[k];
    if (v !== undefined && (typeof v !== 'string' || !choices.includes(v))) {
      out.push({ field: k, message: `valeur inconnue (${choices.join(', ')})` });
    }
  }
  return out;
}

// ==================== Version du mod dans les images ====================

/** Compare deux versions « 0.0.2 » : négatif si a est plus ancienne, null si l'une ne se lit pas. */
export function compareVersions(a: string, b: string): number | null {
  const parse = (v: string) => v.trim().replace(/^v/i, '').split('.').map((p) => (/^\d+$/.test(p) ? Number(p) : Number.NaN));
  const pa = parse(a);
  const pb = parse(b);
  if (pa.some(Number.isNaN) || pb.some(Number.isNaN)) return null;
  for (let i = 0; i < Math.max(pa.length, pb.length); i++) {
    const d = (pa[i] ?? 0) - (pb[i] ?? 0);
    if (d !== 0) return d;
  }
  return 0;
}

export interface VersionGap {
  image: CatalogImage;
  /** older : l'image porte un mod plus ancien que la table ; newer : plus récent ; unknown : version illisible. */
  kind: 'older' | 'newer' | 'unknown';
}

/** Images dont le mod n'est pas à la version de la table du serveur. */
export function versionGaps(catalog: WeaponsCatalog): VersionGap[] {
  const out: VersionGap[] = [];
  for (const image of catalog.images) {
    const cmp = image.version === null ? null : compareVersions(image.version, catalog.tableVersion);
    if (cmp === null) out.push({ image, kind: 'unknown' });
    else if (cmp < 0) out.push({ image, kind: 'older' });
    else if (cmp > 0) out.push({ image, kind: 'newer' });
  }
  return out;
}
